/*
   Figures both ways. A run's summary carries its figures over every expert mark (the figures of
   record) and, once its marks are tagged, the same figures over the in-scope marks only, under an
   `in_scope_` prefix. Per KPI it may carry `kpi_<id>_safety_recall` (caught or escalated, the
   figure of record), `kpi_<id>_recall` (caught only) and `kpi_<id>_n`, and their in-scope twins;
   a KPI with too few marks to measure carries a null recall. Every key is optional: a run published before the tags has none of them, and a missing
   figure is shown as missing, never as a zero.
*/

import { OUT_OF_SCOPE, type EvalMark, type OutOfScope } from "./eval-runs-data"

export type Summary = Readonly<Record<string, number | null>>
type Figure = number | null | undefined

export const IN_SCOPE_PREFIX = "in_scope_"

/** Whether the run was scored over in-scope marks too, so excluding them means anything. */
export function hasScopeFigures(summary: Summary): boolean {
  // `mark_scope_loaded` says outright whether the tags were read; older summaries only imply it.
  const loaded = summary["mark_scope_loaded"]
  if (loaded !== undefined && loaded !== 1) return false
  return Object.keys(summary).some((k) => k.startsWith(IN_SCOPE_PREFIX))
}

/** A figure as shown: the one the switch picked, and the other beside it when the run has both. */
export interface FigurePair {
  primary: Figure
  /** The other view of the same figure; absent when the run carries only one. */
  secondary?: { value: Figure; label: string }
  /** Whether the primary is the in-scope figure. */
  inScope: boolean
}

/**
 * One summary figure, picked by the switch, with its counterpart when the run has one.
 *
 * Gated on `hasScopeFigures` rather than on the twin key alone: a summary that says its tags were
 * not read (`mark_scope_loaded` other than 1) may still carry `in_scope_` keys, and pairing them
 * would print "(in scope …)" beside a switch that says the run has no scope tags.
 */
export function pairFigure(summary: Summary, key: string, exclude: boolean): FigurePair {
  const all = summary[key]
  const scopedKey = IN_SCOPE_PREFIX + key
  if (!hasScopeFigures(summary) || !(scopedKey in summary)) return { primary: all, inScope: false }
  const scoped = summary[scopedKey]
  return exclude
    ? { primary: scoped, secondary: { value: all, label: "all marks" }, inScope: true }
    : { primary: all, secondary: { value: scoped, label: "in scope" }, inScope: false }
}

export interface KpiRow {
  id: string
  /** Caught or escalated: the KPI's figure of record. */
  safetyRecall: FigurePair
  /** Caught only. */
  recall: FigurePair
  n: FigurePair
  /** Marks this KPI lost to the exclusion: all-marks n less in-scope n, when both are known. */
  excluded: Figure
}

// Lazy, so `kpi_8_9_safety_recall` reads as KPI `8_9` rather than `8_9_safety`.
const KPI_KEY = /^kpi_(.+?)_(safety_recall|recall|n)$/

/** The per-KPI rows the summary carries, in KPI order; empty when it carries none. */
export function kpiRows(summary: Summary, exclude: boolean): KpiRow[] {
  const ids = new Set<string>()
  for (const key of Object.keys(summary)) {
    const m = KPI_KEY.exec(key)
    if (m?.[1]) ids.add(m[1])
  }
  return [...ids]
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .map((id) => {
      const all = summary[`kpi_${id}_n`]
      const scoped = hasScopeFigures(summary) ? summary[`${IN_SCOPE_PREFIX}kpi_${id}_n`] : undefined
      return {
        id,
        safetyRecall: pairFigure(summary, `kpi_${id}_safety_recall`, exclude),
        recall: pairFigure(summary, `kpi_${id}_recall`, exclude),
        n: pairFigure(summary, `kpi_${id}_n`, exclude),
        excluded: typeof all === "number" && typeof scoped === "number" ? all - scoped : undefined,
      }
    })
}

/** A KPI id as read: `8_9` is KPI 8.9; anything else is shown as sent. */
export function kpiName(id: string): string {
  return /^\d+(_\d+)+$/.test(id) ? id.replaceAll("_", ".") : id
}

/** How many marks each tag took out of the in-scope figures; null when the run does not say. */
export function excludedByTag(summary: Summary): { tag: OutOfScope; n: Figure }[] | null {
  if (!hasScopeFigures(summary)) return null
  const counts = OUT_OF_SCOPE.map((tag) => ({ tag, n: summary[`excluded_${tag}_n`] }))
  return counts.some((c) => c.n !== undefined) ? counts : null
}

/** The marks shown: every one, or only the in-scope ones while the exclusion is on. */
export function visibleMarks(marks: readonly EvalMark[], exclude: boolean): readonly EvalMark[] {
  return exclude ? marks.filter((m) => m.scope === "in_scope") : marks
}

/** One line for the mark list: who settled the scope. Nothing for a mark nobody tagged. */
export function scopeSourceShort(mark: EvalMark): string | null {
  if (mark.scopeSource === "ai") return "AI"
  if (mark.scopeSource === "admin") return `Edited by ${mark.scopeSetByName ?? "an admin"}`
  return null
}
